import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import { scaled } from "../pictureScale";
import type { UiTheme } from "../theme";
import type { ChartSeriesAnnotation } from "./chartAnnotations";

// Split out of `WagmiLineChart.tsx` (the 500-line cap). The range + per-
// series current-value footer, shown when `showMeta` is on, with the "which
// curve" swatch beside each number.

type Props = {
  built: ChartSeriesAnnotation[];
  theme: UiTheme;
  lo: number;
  hi: number;
  yFormat: (y: number) => string;
};

export function WagmiChartLegend({ built, theme, lo, hi, yFormat }: Props) {
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <View style={styles.meta}>
      <Text style={styles.range}>{`${yFormat(lo)} – ${yFormat(hi)}`}</Text>
      <View style={styles.currents}>
        {built
          .filter((b) => b.dots.length > 0)
          .map((b) => (
            <View key={`cur-${b.key}`} style={styles.current}>
              <View
                style={
                  b.variant === "secondary"
                    ? [styles.swatch, styles.swatchDashed]
                    : b.variant === "focal"
                      ? [styles.swatch, styles.swatchFocal]
                      : styles.swatch
                }
              />
              <Text style={b.variant === "secondary" ? styles.curMuted : styles.curStrong}>
                {yFormat(b.last)}
              </Text>
            </View>
          ))}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: t, spacing: s } = theme;
  return StyleSheet.create({
    meta: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingTop: 2 },
    range: { color: p.ink3, fontSize: t.sizes.xs, fontVariant: ["tabular-nums"] },
    currents: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    current: { flexDirection: "row", alignItems: "center", gap: s.s1 },
    // PG-46/PG-48: a FILLED rectangle in the curve's own colour, traced by a
    // thin `ink` border (WCAG G195, the fill alone can be under 3:1). 20 wide
    // so the dash reads; solid vs dashed is the border style.
    swatch: {
      width: scaled(theme, 20),
      height: scaled(theme, 12),
      borderWidth: scaled(theme, 1.5),
      borderColor: p.ink,
      backgroundColor: p.ink,
    },
    swatchDashed: { backgroundColor: p.ink3, borderStyle: "dashed" },
    swatchFocal: { backgroundColor: p.brand500 },
    curStrong: { color: p.ink, fontSize: t.sizes.xs, fontWeight: "700", fontVariant: ["tabular-nums"] },
    curMuted: { color: p.ink3, fontSize: t.sizes.xs, fontWeight: "600", fontVariant: ["tabular-nums"] },
  });
}
